import {Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { ICategoryTarget, IModelAllocationChartConfig } from '../../irb-editors.model';
import { IDropdownItem } from '@irb/irb-common/models/dropdown.model';
import { UtilityService } from '../../../irb-common/services/utility.service';
import { EditorModelService } from '../editor-model.service';
import { AppService } from '@irb/app.service';
import { DropdownFilterComponent } from '@irb-common/components/dropdown-filter/dropdown-filter.component';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'irb-security-allocations-chart',
  templateUrl: './security-allocations-chart.component.html',
  styleUrls: ['./security-allocations-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class SecurityAllocationsChartComponent implements OnInit, OnDestroy {

  chartData: any;
  categoryTargetPercent: Array<ICategoryTarget> = [];
  colors: Array<string>;
  private editDataChangeSubscription: Subscription;
  objectOfCategoryLists: any = {};

  @ViewChild('chartStyleDropdown') chartStyleDropdown: DropdownFilterComponent;
  @Input() showCustomView: boolean = false;
  @Output() showCustomViewChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  chartStyle: IDropdownItem;
  config: IModelAllocationChartConfig;
  expanded: Object;

  options: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const decimal = this.modelType === 'Blended' ? 2 : 1;
            const label = context.label;
            const value = Number(context.dataset.data[context.dataIndex]).toFixed(decimal);
            const retVal = this.noTarget ? label : label + " " + value + "%";
            return retVal;
          }
        }
      }
    }
  };

  @Input()
  modelType: string;

  @Input()
  categoryLabel: string = 'CustCat';

  @Input()
  noTarget: boolean = false;


  constructor(
    private util: UtilityService,
    private editorModelService: EditorModelService,
    private appService: AppService
  ) {
    this.config = {
      showPositions: true,
      showSAC: true,
      showSACLevel1: true,
      showSACLevel2: false,
      showSACLevel3: false,
      showSACLevel4: false,
      showCustCat: true,
      showMorningstarStockStyles: true,
      chartStyle: 'BAR_CHART'
    };

    this.expanded = {
      "POSITION": true,
      "CUST_CAT": true,
      "SAC": true,
      "MSS": true
    };

    let c =  window.localStorage.getItem("MODEL_ALLOCATION_CHART_CONFIG");
    this.config = (c) ? JSON.parse(c) : this.config;

    let e =  window.localStorage.getItem("MODEL_ALLOCATION_CHART_EXPAND");
    this.expanded = (e) ? JSON.parse(e) : this.expanded;

    this.chartStyle = (this.config.chartStyle == 'BAR_CHART') ? {name: "Bar Chart", value: "BAR_CHART"} : {name: "Donut Chart", value: "PIE_CHART"};
  }

  ngOnInit(): void {
    this.update();

    this.editDataChangeSubscription = this.editorModelService.parametersChanged.subscribe(()=> {
      this.colors = [];
      this.categoryTargetPercent = [];
      this.update();
    });
  }

  update() {
    this.colors = this.editorModelService.getChatColors();
    let categories = this.editorModelService.getCategories();
    let percentages = this.editorModelService.getPercentages();
    this.categoryTargetPercent = this.editorModelService.getCategoryTargetPercent();
    if (categories.length === 0) {
      this.colors = ['#93000F'];
      categories = ['Under-Allocated'];
      percentages = [100];
      this.categoryTargetPercent = [{ 'category': 'Under-Allocated', 'targetPercent': 100,  colorCode: '#93000F'}];
      let lists: any = [];
      lists[0] = this.categoryTargetPercent;
      this.objectOfCategoryLists = {
        numOfLists: 1,
        lists: lists
      };
    } else {
      this.buildObjectOfCategoryLists();
    }

    this.chartData = {
      labels: categories,
      datasets: [
        {
          data: percentages,
          backgroundColor: this.colors,
          hoverBackgroundColor: this.colors,
          borderWidth: 2,
          hoverBorderColor: ["#FFFFFF"]
        }
      ]
    };
    if (!this.noTarget) {
      const globSet: any = this.editorModelService.getGlobalSettings();
      if (globSet && globSet.securityCustAttrAbbr) {
        this.categoryLabel = globSet.securityCustAttrAbbr;
      }
    }
  }

  private buildObjectOfCategoryLists() {
    const copyData = JSON.parse(JSON.stringify(this.categoryTargetPercent));
    copyData.sort((a: any, b: any) => (Number(a.targetPercent) < Number(b.targetPercent)) ? 1 : -1);
    const numOfLists = Math.ceil(copyData.length / 10);
    let listsOfAll: any = [],
        singleList: any = [],
        startIdx: number,
        endIdx: number;
    for (let i = 0; i < numOfLists; i++) {
      startIdx = i * 10;
      endIdx = i * 10 + 10;
      singleList = copyData.slice(startIdx, endIdx);
      listsOfAll[i] = singleList;
    }
    // console.log(listsOfAll);
    this.objectOfCategoryLists = {
      numOfLists: 1,
      lists: listsOfAll
    };

  }

  getColorByCategory(category: string) {
      return this.editorModelService.getColorByCategory(category);
  }

  setChartStyleDropdown(_searchStr: any) {
      this.chartStyleDropdown.set([
          {name: "Bar Chart", value: "BAR_CHART"},
          {name: "Donut Chart", value: "PIE_CHART"}
      ]);
  }

  onInputChange() {
    window.localStorage.setItem("MODEL_ALLOCATION_CHART_CONFIG", JSON.stringify(this.config));
    window.localStorage.setItem("MODEL_ALLOCATION_CHART_EXPAND", JSON.stringify(this.expanded));
    this.config = { ...this.config };
  }

  onChartStyleChange() {
      this.config.chartStyle = this.chartStyle.value;
      this.onInputChange();
  }

  toggleCustomView() {
    this.showCustomView = !this.showCustomView;
    this.showCustomViewChange.emit(this.showCustomView);
  }

  hideCustomView() {
    this.showCustomView = false;
    this.showCustomViewChange.emit(false);
  }

  expandAll() {
    for(const key in this.expanded) {
      this.util.setObjectValue(this.expanded, key, true);
    }
    this.onInputChange();
  }

  collapseAll() {
    for(const key in this.expanded) {
      this.util.setObjectValue(this.expanded, key, false);
    }
    this.onInputChange();
  }

  ngOnDestroy(): void {
    if (this.editDataChangeSubscription) {
      this.editDataChangeSubscription.unsubscribe();
    }
  }
}
